import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef } from 'react';
import { useAgGridCommon } from 'common/table/agGrid/AgGridCommon';
import columnDefs, { MATCH_FILTER_FIELDS, MemberGridRow } from './columnDefs';
import {
  commonPaginationOptions,
  getExternalFilterColMainMenuItems,
  mergeGridOpts,
} from 'utils/agGrid/options';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import { ServerSideQuery } from 'utils/agGrid/types';
import { fetchMembersPromise, selectMember } from 'reducers/membersReducer';
import useExternalFilter from 'utils/agGrid/hooks/useExternalFilter';
import { FilterWrapper, Wrapper } from './MembersGrid.styles';
import Member from 'ts/models/Member.model';
import history from 'constants/history';
import ExternalFilters from './ExternalFilters';
import { processSSRM } from 'utils/agGrid/ssrm/processSSRM';

const opts = mergeGridOpts(commonPaginationOptions, {
  rowStyle: { cursor: 'pointer' },
  getMainMenuItems: getExternalFilterColMainMenuItems,
});

export const MEMBERS_GRID_NAME = 'members';

type FetchMembersRequest = ServerSideQuery & {
  includeName: string;
};

const MembersGrid = () => {
  const gridRef = useRef<AgGridReact>(null);
  const dispatch = useAppDispatch();

  const { gridVisible, initialGridSettings, onStateUpdated } = useAgGridCommon({
    gridName: MEMBERS_GRID_NAME,
  });

  const fetchData = useCallback(
    async (params: IServerSideGetRowsParams) => {
      const { request } = params;
      const parsed = parseAGRequest(request, { transformAttrs: true });
      const gridFilters = parsed?.filter || [];

      const body: FetchMembersRequest = {
        ...parsed,
        filter: gridFilters.map((filter) => {
          if (MATCH_FILTER_FIELDS.includes(filter.attr)) {
            return { ...filter, op: 'match' };
          }
          return filter;
        }),
        includeName: 'true',
      };
      const response = await fetchMembersPromise(body, dispatch);
      return {
        rows: response?.members,
        count: response.count,
      };
    },
    [dispatch],
  );

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) =>
      processSSRM<Member>(params, fetchData),
    [fetchData],
  );

  const onGridReadyCallback = useCallback(
    (params: GridReadyEvent) => {
      const dataSource: IServerSideDatasource = {
        getRows,
      };
      params.api?.setGridOption('serverSideDatasource', dataSource);
    },
    [getRows],
  );

  const options = useMemo(
    () => ({ ...opts, onGridReady: onGridReadyCallback }),
    [onGridReadyCallback],
  );

  const onRowClicked = useCallback(
    ({ data }: { data: Member }) => {
      const memberId = data?.memberId;
      if (memberId) {
        dispatch(selectMember(memberId));
        history.push(`/members/${memberId}`);
      }
    },
    [dispatch],
  );

  const externalFilterProps = useExternalFilter<MemberGridRow>({
    gridRef,
    options,
  });

  return (
    <>
      {gridVisible ? (
        <Wrapper>
          <FilterWrapper>
            <ExternalFilters {...externalFilterProps} />
          </FilterWrapper>
          <div className="ag-theme-quartz">
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              gridOptions={externalFilterProps.gridOptions}
              onStateUpdated={onStateUpdated}
              initialState={initialGridSettings}
              rowModelType="serverSide"
              onRowClicked={onRowClicked}
            />
          </div>
        </Wrapper>
      ) : null}
    </>
  );
};

export default MembersGrid;
